import { Injectable, signal } from '@angular/core';

/**
 * Loads mermaid (~3 MB) on first need instead of shipping it in the initial page load.
 *
 * ngx-markdown reads `mermaid` off the global synchronously and throws when it is missing -- even for
 * a message with no diagram in it -- so its `[mermaid]` input must stay false until `loaded()` is true.
 * The `import('mermaid')` specifier matches pure-ui-copilot's own loader, so both share one lazy chunk.
 */
@Injectable({ providedIn: 'root' })
export class MermaidLoaderService {
    private readonly loadedState = signal(false);
    readonly loaded = this.loadedState.asReadonly();

    private pending: Promise<void> | null = null;
    // Browsers cache a failed dynamic import for the life of the page (whatwg/html#6768), so once the
    // chunk has failed a retry would only fail again -- and log again for every message rendered.
    private failed = false;

    /**
     * Starts the load if needed. Never rejects: if the chunk fails to load, the error is logged once,
     * diagrams stay unrendered (the message still renders as markdown) until the page is reloaded.
     */
    ensureLoaded(): Promise<void> {
        if (this.loadedState()) {
            return Promise.resolve();
        }
        const scope = this.globalScope();
        if (scope.mermaid) {
            this.loadedState.set(true);
            return Promise.resolve();
        }
        if (this.failed) {
            return Promise.resolve();
        }
        this.pending ??= this.importMermaid()
            .then(({ default: mermaid }) => {
                scope.mermaid = mermaid;
                this.loadedState.set(true);
            })
            .catch((err: unknown) => {
                this.failed = true;
                console.error('Failed to load mermaid; Copilot diagrams will not render', err);
            })
            .finally(() => {
                this.pending = null;
            });
        return this.pending;
    }

    protected importMermaid(): Promise<{ default: unknown }> {
        return import('mermaid');
    }

    protected globalScope(): { mermaid?: unknown } {
        return window as unknown as { mermaid?: unknown };
    }
}
